import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { subscribeServiceRequests, updateServiceRequestStatus, deleteServiceRequest } from '../../services/db';
import { notifyInternal, notifyCustomer } from '../../services/email';
import { requestStatusEmail } from '../../services/emailTemplates';

const statusColors = {
  Pending: 'warning',
  'In Progress': 'info',
  Completed: 'success',
  Cancelled: 'error'
};

function ServiceRequests({ searchQuery, refreshKey }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [requests, setRequests] = useState([]);

  // Real-time Firestore listener with localStorage fallback
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeServiceRequests((data) => {
        setRequests(data);
        // Keep localStorage in sync as a local cache
        localStorage.setItem('serviceRequests', JSON.stringify(data));
      });
    } catch (err) {
      console.error('Firestore listener failed — falling back to localStorage:', err);
      const saved = JSON.parse(localStorage.getItem('serviceRequests') || '[]');
      setRequests(saved.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    }
    return () => unsubscribe();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDetailsOpen = () => {
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
  };

  const handleStatusChange = async (newStatus) => {
    const request = selectedRequest;
    handleMenuClose();
    if (request) {
      await updateServiceRequestStatus(request._docId, newStatus);
      try {
        const firstName = request.customerName.split(' ')[0];
        const device = [request.deviceType, request.deviceBrand, request.deviceModel].filter(Boolean).join(' — ');
        await notifyInternal(
          `Service Request ${request.id} — Status: ${newStatus}`,
          `Customer: ${request.customerName} <${request.email}>\nPhone: ${request.phone || 'N/A'}\n\nStatus: ${newStatus}\nDevice: ${device}\nIssue: ${request.issue}\nAppointment: ${request.date} at ${request.time}`,
          request.email,
        );
        await notifyCustomer(
          request.email,
          `Your Service Request ${request.id} — ${newStatus} | AkdmeTech`,
          requestStatusEmail({
            firstName,
            requestId: request.id,
            status:    newStatus,
            device,
            issue:     request.issue,
            date:      request.date,
            time:      request.time,
          }),
        );
      } catch (error) {
        console.error('Failed to send email notification:', error);
      }
    }
  };

  const handleDeleteRequest = async () => {
    const request = selectedRequest;
    handleMenuClose();
    if (request) {
      await deleteServiceRequest(request._docId);
      try {
        await notifyInternal(
          `Service Request ${request.id} — Cancelled`,
          `Customer: ${request.customerName} <${request.email}>\n\nRequest ID: ${request.id} has been deleted.\nDevice: ${request.deviceType} — ${request.deviceBrand} ${request.deviceModel}\nIssue: ${request.issue}`,
          request.email,
        );
      } catch (error) {
        console.error('Failed to send email notification:', error);
      }
    }
  };

  const filteredRequests = requests.filter(request => {
    const searchLower = searchQuery.toLowerCase();
    return (
      request.customerName.toLowerCase().includes(searchLower) ||
      request.email.toLowerCase().includes(searchLower) ||
      request.id.toLowerCase().includes(searchLower) ||
      request.deviceType.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>{request.id}</TableCell>
                  <TableCell>{`${request.date} ${request.time}`}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2">{request.customerName}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {request.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {request.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.deviceType}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request.deviceBrand} {request.deviceModel}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status}
                      color={statusColors[request.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, request)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={filteredRequests.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDetailsOpen}>View Details</MenuItem>
        <MenuItem onClick={() => handleStatusChange('In Progress')}>Mark In Progress</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Completed')}>Mark Completed</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Cancelled')}>Cancel Request</MenuItem>
        <MenuItem onClick={handleDeleteRequest} sx={{ color: 'error.main' }}>Delete Request</MenuItem>
      </Menu>

      {selectedRequest && (
        <Dialog
          open={detailsOpen}
          onClose={handleDetailsClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(45, 52, 54, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <DialogTitle>
            Service Request Details - {selectedRequest.id}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Customer Information</Typography>
                <Typography variant="body1">{selectedRequest.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedRequest.email}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedRequest.phone}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Device Information</Typography>
                <Typography variant="body1">{selectedRequest.deviceType}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRequest.deviceBrand} {selectedRequest.deviceModel}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Issue Description</Typography>
                <Typography variant="body1">{selectedRequest.issue}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Appointment</Typography>
                <Typography variant="body1">{selectedRequest.date} {selectedRequest.time}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedRequest.status}
                  color={statusColors[selectedRequest.status]}
                  size="small"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default ServiceRequests;
